import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { organizationService } from '../../services/organizationService';
import {
  Container,
  Box,
  Avatar,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Alert,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { FirebaseError } from 'firebase/app';
import AcceptInvitation from '../../components/organization/AcceptInvitation';

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await signIn(formData.email, formData.password);
      
      try {
        // Check for pending invitations before showing the dialog
        const invitations = await organizationService.findOrganizationsByInvitationEmail(formData.email);
        if (invitations.length > 0) {
          setShowInvitationDialog(true);
        } else {
          navigate('/dashboard');
        }
      } catch (invitationErr) {
        console.error('Error checking invitations:', invitationErr);
        // En cas d'erreur lors de la vérification des invitations, on redirige quand même vers le dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            setError(t('auth.errors.invalidCredentials'));
            break;
          case 'auth/invalid-email':
            setError(t('auth.errors.invalidEmail'));
            break;
          case 'auth/user-disabled':
            setError(t('auth.errors.accountDisabled'));
            break;
          case 'auth/too-many-requests':
            setError(t('auth.errors.tooManyAttempts'));
            break;
          default:
            setError(err.message);
        }
      } else {
        setError(t('auth.errors.signInFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvitationClose = () => {
    setShowInvitationDialog(false);
    navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: 1,
            p: 4
          }}
        >
        <Box
          component="img"
          src="/logo-verifleet1.png"
          alt="VeriFleet Logo"
          sx={{
            width: '280px',
            height: 'auto',
            mb: 4
          }}
        />
        <Box sx={{ typography: 'h5', component: 'h1', mb: 3 }}>
          {t('auth.login')}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('auth.email')}
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            error={Boolean(error && error.includes('email'))}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={t('auth.password')}
            type="password"
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            error={Boolean(error && error.includes('password'))}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? t('auth.signingIn') : t('auth.login')}
          </Button>
          <Grid container>
            <Grid item xs>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                {t('auth.forgotPassword')}
              </Link>
            </Grid>
            <Grid item>
              <Link component={RouterLink} to="/signup" variant="body2">
                {t('auth.noAccount')}
              </Link>
            </Grid>
          </Grid>
        </Box>
        </Box>

        {/* Invitation Dialog */}
        <AcceptInvitation
          open={showInvitationDialog}
          onClose={handleInvitationClose}
          email={formData.email}
        />
      </Container>
    </Box>
  );
};

export default Login;
